<template>
  <div class="draft-orders-dropdown">
    <Loading :is-loading="isDeleting" />
    <header
      v-if="withTopActionButton"
      class="intro"
    >
      <h2 class="intro__title">
        Switch active order
      </h2>
      <p class="intro__text">
        Anything you add to the cart goes into the active order.
      </p>
    </header>

    <div
      v-if="loadingGetDraftOrders"
      class="d-flex justify-content-center py-4"
    >
      <moon-loader
        color="black"
        size="85px"
      />
    </div>

    <ul
      v-else
      ref="dropdown"
      class="order-list"
      role="radiogroup"
      aria-label="Draft orders"
      @scroll="handleScroll"
    >
      <li
        v-for="order in draftOrdersList"
        :key="order.id"
        class="order-row"
        :class="{ 'is-active': isActiveOrder(order), 'is-locked': order.already_active, 'has-delete': isCopyDraftOrder }"
      >
        <button
          type="button"
          role="radio"
          class="order-row__select"
          :aria-checked="String(isActiveOrder(order))"
          :aria-disabled="String(!!order.already_active)"
          @click="onDraftOrderItemClicked(order)"
        >
          <span class="radio" aria-hidden="true" />
          <span class="order-row__body">
            <span class="order-row__title">
              <strong>#{{ order.id }}</strong> · {{ order.company?.name }}{{ order.is_digital_catalog ? ' (DC)' : '' }}
            </span>
            <span class="order-row__meta">
              <span v-if="order.buyer?.name" class="order-row__buyer">{{ order.buyer.name }}</span>
              <span class="order-row__ship"><template v-if="order.buyer?.name">·&nbsp;</template>{{ orderMeta(order) }}</span>
            </span>
            <span v-if="order.already_active" class="order-row__lock">
              <fa icon="lock" /> In use by {{ order.active_by_user?.name || 'another user' }}
            </span>
          </span>
          <span v-if="isActiveOrder(order)" class="active-badge">Active</span>
        </button>
        <button
          v-if="isCopyDraftOrder"
          type="button"
          class="order-row__delete"
          :aria-label="`Delete draft #${order.id}`"
          :disabled="isDeleting"
          @click="onDeleteDraftOrderItemClicked(order)"
        >
          <fa icon="trash-alt" fixed-width />
        </button>
      </li>
    </ul>

    <footer
      v-if="withTopActionButton"
      class="actions"
    >
      <button
        type="button"
        class="actions__new"
        @click="openNewOrderModal"
      >
        <span aria-hidden="true">+</span> New draft order
      </button>
      <router-link
        :to="{ name: 'order-history.draft' }"
        class="actions__all"
        @click.native="$emit('closeDropdown')"
      >
        All orders
      </router-link>
    </footer>
  </div>
</template>

<script>
import MoonLoader from 'vue-spinner/src/MoonLoader.vue'
import dayjs from 'dayjs'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import orderRelated from '~/mixins/orderRelated'
import Loading from '~/components/core/Loading.vue'

export default {
  name: 'DraftOrdersDropdown',
  components: {
    MoonLoader,
    Loading
  },
  mixins: [orderRelated],
  props: {
    withTopActionButton: {
      type: Boolean,
      default: true
    },
    loadingGetDraftOrders: {
      type: Boolean,
      default: false
    },
    isCopyDraftOrder: {
      type: Boolean,
      default: true
    },
    productIds: {
      type: Array,
      default: () => {}
    },
    actionType: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      isDeleting: false
    }
  },
  created () {
    // Load the lightweight draft feed as soon as the dropdown mounts (covers the
    // catalog / tag / collections embeds). The Header also triggers it on popper-open;
    // the store's seq stale-guard makes the possible double-dispatch harmless.
    this.fetchDraftOrdersList()
  },
  computed: {
    ...mapGetters({
      draftOrdersList: 'orders/draftOrdersList',
      activeDraftOrder: 'orders/activeDraftOrder',
      isAdmin: 'auth/isAdmin',
      taggedProducts: 'catalog_products/taggedProducts',
      selectedPopularTagsId: 'catalog_products/getSelectedPopularTagsId',
      currentPage: 'catalog_products/getCurrentPage',
      selectedCollection: 'catalog_products/selectedCollection',
      draftListPage: 'orders/draftListPage',
      draftListTotalPages: 'orders/draftListTotalPages'
    }),
    ...mapState({
      popularTags: state => state.tags.popularTags
    })
  },
  methods: {
    ...mapActions({
      fetchOrders: 'orders/fetchOrders',
      fetchDraftOrdersList: 'orders/fetchDraftOrdersList',
      fetchProductItemsOrderSummary: 'checkout/fetchProductItemsOrderSummary',
      deleteOrderById: 'orders/deleteOrderById',
      clearInOrderOutOrder: 'catalog_products/clearInOrderOutOrder',
      fetchCatalogProducts: 'catalog_products/fetchCatalogProducts',
      copyDraftOrderToNewDraftOrder: 'orders/copyDraftOrderToNewDraftOrder',
      deleteBulkProductFromOrder: 'checkout/deleteBulkProductFromOrder',
      getUnitSummaryReportData: 'orders/getUnitSummaryReportData',
      removeQuantityExceed: 'checkout/removeQuantityExceed'
    }),
    ...mapMutations({
      clearActiveCollection: 'catalog_products/CLEAR_ACTIVE_COLLECTION',
      resetOrderFlags: 'catalog_products/RESET_ORDER_FLAGS',
      setNewOrderMode: 'orders/SET_NEW_ORDER_MODE',
      clearActiveDraftOrder: 'orders/CLEAR_ACTIVE_DRAFT_ORDER'
    }),
    async handleScroll () {
      const dropdown = this.$refs.dropdown
      const bottom = dropdown.scrollHeight - dropdown.scrollTop === dropdown.clientHeight
      if (bottom && this.draftListPage < this.draftListTotalPages) {
        await this.fetchDraftOrdersList({ page: this.draftListPage + 1 })
      }
    },
    getStartShipDateOrder (value) {
      return dayjs(value).format('MM/DD/YYYY')
    },
    isActiveOrder (order) {
      return order.id === this.activeDraftOrder?.id
    },
    orderMeta (order) {
      const styles = order.styles_count ?? 0
      return `Ships ${this.getStartShipDateOrder(order.start_ship_date)} · ${styles} ${styles === 1 ? 'style' : 'styles'}`
    },
    openNewOrderModal () {
      this.$modal.show('new-order-modal')
      this.$emit('closeDropdown')
    },
    async onDraftOrderItemClicked (order) {
      // Check if order is already active by another user
      if (order.already_active) {
        this.$toast.error(`This draft order is currently active by ${order.active_by_user?.name || 'another user'}`)
        return
      }

      if (this.isCopyDraftOrder === false) {
        if (order.id === this.activeDraftOrder?.id) {
          return
        }

        const payload = {
          active_order_id: this.activeDraftOrder?.id,
          draft_order_id: order.id,
          product_ids: this.productIds
        }

        if (this.actionType === 'copy') {
          const message = await this.copyDraftOrderToNewDraftOrder(payload)
          this.$toast.success(message)
          this.$emit('hideModal')
          return
        }

        if (this.actionType === 'move') {
          await this.copyDraftOrderToNewDraftOrder(payload)
          const orderId = this.activeDraftOrder?.id
          await this.deleteBulkProductFromOrder({ orderId, productIds: this.productIds })
          this.setNewOrderMode(order.id)
          this.$emit('hideModal')
          this.$toast.success('Successfully Moved to the new order.')
          await this.fetchOrders()
          await this.fetchProductItemsOrderSummary()
        }

        return
      }

      this.setNewOrderMode(order.id)
      this.$emit('closeDropdown')
      await this.fetchOrders()
      await this.fetchProductItemsOrderSummary()
      await this.getUnitSummaryReportData(order.id)
      await this.clearInOrderOutOrder()
      if (this.$route.name === 'tag-module') {
        /** TODO : get selected popular tag's or my tags products */
        const { selected, ...filteredParams } = { ...this.$route.query }
        selected === 'my_tags' ? delete filteredParams.tag_ids : delete filteredParams.my_tags_only
        filteredParams.page = 1
        const apiRoute = `/api/product?${new URLSearchParams(filteredParams).toString()}`
        this.fetchCatalogProducts({ urlEndpoint: apiRoute, isChange: true })
      } else if (this.$route.name === 'collections') {
        this.fetchCatalogProducts({ urlEndpoint: `/api/product?collection_id=${this.selectedCollection?.id}`, isCollectionsPage: true })
      } else if (this.$route.name === 'products') {
        const params = { ...this.$route.query }
        params.page = this.currentPage
        await this.fetchCatalogProducts({ urlEndpoint: `/api/product?${new URLSearchParams(params).toString()}` })
      }
    },
    async onDeleteDraftOrderItemClicked (order) {
      try {
        this.isDeleting = true
        // Delete the order
        const response = await this.deleteOrderById({ orderId: order.id })
        // Check if the deleted order is the active draft order
        if (order.id === this.activeDraftOrder?.id) {
          // Clear the active draft order and related data in the store
          this.clearActiveDraftOrder()
          this.resetOrderFlags()
          if (this.$route.name !== 'products' && this.$route.name.includes('checkout-proceed')) {
            this.$router.push({ name: 'products' })
          } else {
            if (this.$route.name === 'tag-module') {
              /** TODO : get selected popular tag's or my tags products */
              const { selected, ...filteredParams } = { ...this.$route.query }
              selected === 'my_tags' ? delete filteredParams.tag_ids : delete filteredParams.my_tags_only
              filteredParams.page = 1
              const apiRoute = `/api/product?${new URLSearchParams(filteredParams).toString()}`
              this.fetchCatalogProducts({ urlEndpoint: apiRoute, isChange: true })
            } else if (this.$route.name === 'collections') {
              this.fetchCatalogProducts({ urlEndpoint: `/api/product?collection_id=${this.selectedCollection?.id}&page=${this.currentPage}`, isCollectionsPage: true })
            } else if (this.$route.name === 'products') {
              const params = { ...this.$route.query }
              params.page = this.currentPage
              await this.fetchCatalogProducts({ urlEndpoint: `/api/product?${new URLSearchParams(params).toString()}` })
            }
          }
        }

        await this.removeQuantityExceed(order.id);
        // Wait for the order to be updated (assuming your fetchOrders action updates the order)
        await this.fetchOrders()
        // Display the success message from the API response
        this.$toast.success(response)
        this.isDeleting = false
      } catch (error) {
        this.isDeleting = false
        this.$toast.error(error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$do-text: #1a1a1a;
$do-muted: #555555;
$do-divider: #e2e2e2;
$do-hover: #f7f7f7;
$do-active-bg: #f1f0ed;
$do-danger: #c01b0d;
$do-gutter: 24px;

.draft-orders-dropdown {
  width: 400px;
  max-width: calc(100vw - 32px);
  color: $do-text;
  font-family: Arial, sans-serif;
  // vue-popper.css centers everything inside .popper
  text-align: left;
}

.intro {
  padding: $do-gutter $do-gutter 20px;
  border-bottom: 1px solid $do-divider;

  &__title {
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
  }

  &__text {
    margin: 0;
    font-size: 13px;
    line-height: 18px;
    color: $do-muted;
  }
}

.order-list {
  list-style: none;
  margin: 0;
  padding: 8px 0;
  max-height: 450px;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.order-row {
  position: relative;
  display: flex;
  align-items: center;

  &:hover {
    background: $do-hover;
  }

  &.is-active {
    background: $do-active-bg;
  }

  &__select {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
    min-width: 0;
    padding: 16px $do-gutter;
    border: 0;
    background: none;
    color: inherit;
    text-align: left;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid $do-text;
      outline-offset: -2px;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 16px;
    line-height: 20px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    strong {
      font-weight: 700;
    }
  }

  &__meta,
  &__lock {
    margin-top: 2px;
    font-size: 13px;
    line-height: 17px;
    color: $do-muted;
  }

  // Ship date and style count matter more than the buyer, so only the buyer truncates.
  &__meta {
    display: flex;
    min-width: 0;
    white-space: nowrap;
  }

  &__buyer {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__ship {
    flex: 0 0 auto;
  }

  &__buyer + &__ship {
    margin-left: 0.3em;
  }

  &__lock {
    font-style: italic;
  }

  &.is-locked &__select {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &__delete {
    position: absolute;
    top: 50%;
    right: 8px;
    width: 44px;
    height: 44px;
    transform: translateY(-50%);
    border: 0;
    background: none;
    color: $do-danger;

    &:disabled {
      opacity: 0.5;
    }
  }

  &.has-delete &__select {
    padding-right: 60px;
  }

  // Delete is secondary to switching orders: on pointer devices it only takes up
  // room while the row is hovered or focused; touch screens always show it.
  @media (hover: hover) {
    &__delete {
      opacity: 0;
    }

    &.has-delete &__select {
      padding-right: $do-gutter;
    }

    &.has-delete:hover &__select,
    &.has-delete:focus-within &__select {
      padding-right: 60px;
    }

    &:hover &__delete,
    &__delete:focus-visible {
      opacity: 1;
    }
  }
}

.radio {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  border: 2px solid $do-text;
  border-radius: 50%;

  .is-active & {
    box-shadow: inset 0 0 0 3px #ffffff, inset 0 0 0 10px $do-text;
  }
}

.active-badge {
  flex: 0 0 auto;
  padding: 3px 10px;
  background: $do-text;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px $do-gutter;
  border-top: 1px solid $do-divider;

  &__new {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    min-height: 44px;
    padding: 0 18px;
    border: 1.5px solid $do-text;
    background: #ffffff;
    color: $do-text;
    font-size: 15px;
    font-weight: 700;

    span {
      font-size: 20px;
      font-weight: 400;
      line-height: 1;
    }

    &:hover {
      background: $do-hover;
    }
  }

  &__all {
    font-size: 14px;
    color: $do-text;
    text-decoration: underline;
  }
}
</style>
